import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import skillApi from '../../api/skillApi';
import { useToast } from '../../contexts/ToastContext';

const EditSkill = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
  });
  const [skillFound, setSkillFound] = useState(true);

  useEffect(() => {
    const fetchSkill = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});
      try {
        const response = await skillApi.getSkillByIdentifier(id);
        if (response.success && response.data) {
          const skillToEdit = response.data;
          setFormData({
            name: skillToEdit.name,
          });
          setSkillFound(true);
        } else {
          setSkillFound(false);
          setGeneralError(response.message || 'Skill not found.');
        }
      } catch (err) {
        console.error('Failed to fetch skill:', err);
        setSkillFound(false);
        setGeneralError(err.response?.data?.message || 'Failed to load skill. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSkill();
  }, [id, addToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    const data = new FormData();
    data.append('name', formData.name);

    try {
      await skillApi.updateSkill(id, data);
      addToast('Skill updated successfully!', 'success');
      navigate('/skills');
    } catch (err) {
      console.error('Failed to update skill:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        setGeneralError(err.response?.data?.message || 'Failed to update skill. Please try again.');
        addToast(err.response?.data?.message || 'Failed to update skill. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (!skillFound) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">Skill Not Found</h1>
        <p className="text-text-gray">{generalError || 'The skill you are trying to edit does not exist.'}</p>
        <Button
          onClick={() => navigate('/skills')}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to Skills List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Edit Skill</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <TextInput
          label="Skill Name"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          required={true}
          placeholder="Skill Name"
          error={validationErrors?.name?.[0]}
        />
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            Update Skill
          </Button>
            <Button
              type="button"
              onClick={() => navigate('/skills')}
              className="bg-gray-500 hover:bg-gray-600 text-white"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    );
  };
  
  export default EditSkill;